import type { InternalNode, Node } from "@xyflow/react";
import { CANVAS_SNAP, snapToGrid } from "../canvas-controls.config";

export type MultiSelectLayoutMode =
  | "align-left"
  | "align-h-center"
  | "align-right"
  | "align-top"
  | "align-v-center"
  | "align-bottom"
  | "tile"
  | "tidy";

export interface NodePositionUpdate {
  nodeId: string;
  position: { x: number; y: number };
}

type GetInternalNode = (id: string) => InternalNode | undefined;

interface AbsRect {
  node: Node;
  x: number;
  y: number;
  w: number;
  h: number;
  right: number;
  bottom: number;
}

function nodeSize(node: Node) {
  return {
    w: node.measured?.width ?? node.width ?? 200,
    h: node.measured?.height ?? node.height ?? 100,
  };
}

function getAbsRect(node: Node, getInternalNode: GetInternalNode): AbsRect {
  const internal = getInternalNode(node.id);
  const abs = internal?.internals?.positionAbsolute ?? node.position;
  const { w, h } = nodeSize(node);
  return {
    node,
    x: abs.x,
    y: abs.y,
    w,
    h,
    right: abs.x + w,
    bottom: abs.y + h,
  };
}

function toParentRelative(
  abs: { x: number; y: number },
  parentId: string | undefined,
  getInternalNode: GetInternalNode,
): { x: number; y: number } {
  if (!parentId) return abs;
  const parent = getInternalNode(parentId);
  const parentAbs = parent?.internals?.positionAbsolute ?? { x: 0, y: 0 };
  return { x: abs.x - parentAbs.x, y: abs.y - parentAbs.y };
}

function snapPos(x: number, y: number) {
  const [gx, gy] = CANVAS_SNAP.grid;
  return {
    x: snapToGrid(x, gx),
    y: snapToGrid(y, gy),
  };
}

export function computeMultiSelectLayout(
  nodes: Node[],
  mode: MultiSelectLayoutMode,
  getInternalNode: GetInternalNode,
): NodePositionUpdate[] {
  if (nodes.length < 2) return [];

  const rects = nodes.map((n) => getAbsRect(n, getInternalNode));
  const minX = Math.min(...rects.map((r) => r.x));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxRight = Math.max(...rects.map((r) => r.right));
  const maxBottom = Math.max(...rects.map((r) => r.bottom));
  const boxW = maxRight - minX;
  const boxH = maxBottom - minY;
  const boxCx = minX + boxW / 2;
  const boxCy = minY + boxH / 2;

  const updates: NodePositionUpdate[] = [];

  if (mode === "tidy" || mode === "tile") {
    const gap = CANVAS_SNAP.grid[0];
    const cols = Math.max(1, Math.ceil(Math.sqrt(rects.length)));
    const sorted = [...rects].sort((a, b) =>
      a.node.id.localeCompare(b.node.id),
    );
    const maxW = Math.max(...sorted.map((r) => r.w));
    const maxH = Math.max(...sorted.map((r) => r.h));
    sorted.forEach((rect, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const absX = minX + col * (maxW + gap);
      const absY = minY + row * (maxH + gap);
      const snapped = snapPos(absX, absY);
      updates.push({
        nodeId: rect.node.id,
        position: toParentRelative(
          snapped,
          rect.node.parentId,
          getInternalNode,
        ),
      });
    });
    return updates;
  }

  for (const rect of rects) {
    let absX = rect.x;
    let absY = rect.y;
    switch (mode) {
      case "align-left":
        absX = minX;
        break;
      case "align-right":
        absX = maxRight - rect.w;
        break;
      case "align-h-center":
        absX = boxCx - rect.w / 2;
        break;
      case "align-top":
        absY = minY;
        break;
      case "align-bottom":
        absY = maxBottom - rect.h;
        break;
      case "align-v-center":
        absY = boxCy - rect.h / 2;
        break;
      default:
        break;
    }
    const snapped = snapPos(absX, absY);
    updates.push({
      nodeId: rect.node.id,
      position: toParentRelative(snapped, rect.node.parentId, getInternalNode),
    });
  }

  return updates;
}

export function computeSelectionBounds(
  nodes: Node[],
  getInternalNode: GetInternalNode,
): {
  minX: number;
  minY: number;
  width: number;
  height: number;
  centerX: number;
  topY: number;
} | null {
  if (nodes.length === 0) return null;
  const rects = nodes.map((n) => getAbsRect(n, getInternalNode));
  const minX = Math.min(...rects.map((r) => r.x));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxRight = Math.max(...rects.map((r) => r.right));
  const maxBottom = Math.max(...rects.map((r) => r.bottom));
  const width = maxRight - minX;
  const height = maxBottom - minY;
  return {
    minX,
    minY,
    width,
    height,
    centerX: minX + width / 2,
    topY: minY,
  };
}

const WRAP_PADDING = 25;

export function computeWrapInSection(
  nodes: Node[],
  getInternalNode: GetInternalNode,
): {
  sectionPosition: { x: number; y: number };
  sectionWidth: number;
  sectionHeight: number;
  parentUpdates: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }>;
} | null {
  const bounds = computeSelectionBounds(nodes, getInternalNode);
  if (!bounds) return null;

  const sectionPosition = snapPos(
    bounds.minX - WRAP_PADDING,
    bounds.minY - WRAP_PADDING,
  );
  const sectionWidth = Math.max(
    200,
    snapToGrid(bounds.width + WRAP_PADDING * 2, CANVAS_SNAP.grid[0]),
  );
  const sectionHeight = Math.max(
    150,
    snapToGrid(bounds.height + WRAP_PADDING * 2, CANVAS_SNAP.grid[1]),
  );

  const parentUpdates = nodes.map((node) => {
    const rect = getAbsRect(node, getInternalNode);
    const absX = rect.x - sectionPosition.x;
    const absY = rect.y - sectionPosition.y;
    return {
      nodeId: node.id,
      parentId: "__pending__",
      position: snapPos(absX, absY),
    };
  });

  return {
    sectionPosition,
    sectionWidth,
    sectionHeight,
    parentUpdates,
  };
}
